import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Wallet, CheckCircle2, Circle, Pencil } from 'lucide-react';
import { getPaymentOptions, setPaymentStatus, changePaymentMethod } from '../../redux/paymentSlice';

// Every value that has ever meant "paid" across the codebase (current + legacy).
const PAID_VALUES = ['Paid', 'paid', 'collected', 'Completed'];
const isPaidStatus = (s) => PAID_VALUES.includes(s);

// Fallback offline methods (used when the resolved options haven't loaded).
const DEFAULT_METHODS = [
  { key: 'cash', label: 'Cash', supportedTiming: 'postpaid' },
  { key: 'card', label: 'Card / Terminal', supportedTiming: 'both' },
  { key: 'online', label: 'Online', supportedTiming: 'both' },
];

// Display labels for legacy method values written by the older completion flow.
// 'paylater' is retired as a choice but kept here so historical records still read.
const LEGACY_LABELS = { Cash: 'Cash', paylater: 'Pay Later', 'Pay Later': 'Pay Later', Netbanking: 'Netbanking' };

// Payment summary + (doctor/admin) controls for a COMPLETED appointment.
// Reads the nested `appointment.payment` object and lets staff toggle Paid/Not-paid
// and change the recorded method — the backend enforces "prepaid can't be cash".
export default function CompletedPaymentCard({ appointment, canEdit, onChanged }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const paymentOptions = useSelector((s) => s.payment?.paymentOptions);
  const mutating = useSelector((s) => s.payment?.mutating || {});
  const [changing, setChanging] = useState(false);

  const appointmentId = appointment?.appointmentId;
  const doctorId = appointment?.doctorId;

  // Mirror the appointment's payment locally so the card reflects a mark-paid /
  // method-change immediately, without waiting for the parent to re-fetch.
  const [localPayment, setLocalPayment] = useState(appointment?.payment);
  useEffect(() => {
    setLocalPayment(appointment?.payment);
  }, [appointment?.payment, appointmentId]);

  const payment = localPayment;
  const timing = payment?.timing || 'postpaid';
  const paid = isPaidStatus(payment?.status);
  const busy = !!mutating[appointmentId];

  useEffect(() => {
    if (canEdit && doctorId) dispatch(getPaymentOptions(doctorId));
  }, [canEdit, doctorId, dispatch]);

  if (!payment) return null;

  // Methods offered for a method change, constrained by the appointment's timing
  // (prepaid excludes at-clinic-only methods like cash). Backend validates too.
  // Drop the retired 'paylater' method if a stale backend config still returns it.
  const allMethods = (paymentOptions?.methods?.length ? paymentOptions.methods : DEFAULT_METHODS)
    .filter((m) => m.key !== 'paylater');
  const methods = allMethods.filter((m) => {
    const st = m.supportedTiming || 'postpaid';
    return st === 'both' || st === timing;
  });

  const methodLabel =
    allMethods.find((m) => m.key === payment.method)?.label ||
    LEGACY_LABELS[payment.method] ||
    payment.method ||
    t('medical_history.na', '—');

  const timingLabel =
    timing === 'prepaid'
      ? t('appointmentmodal.payment_type_prepaid', 'Before the visit')
      : t('appointmentmodal.payment_type_postpaid', 'After the visit');

  const togglePaid = async () => {
    try {
      const res = await dispatch(setPaymentStatus({ appointmentId, paid: !paid })).unwrap();
      if (res?.payment) setLocalPayment(res.payment);
      toast.success(
        !paid
          ? t('payments.marked_paid', 'Marked as paid')
          : t('payments.marked_unpaid', 'Marked as not paid')
      );
      onChanged?.();
    } catch (e) {
      toast.warn(typeof e === 'string' ? e : t('payments.update_failed', 'Could not update payment'));
    }
  };

  const pickMethod = async (key) => {
    if (key === payment.method) { setChanging(false); return; }
    try {
      const res = await dispatch(changePaymentMethod({ appointmentId, method: key })).unwrap();
      if (res?.payment) setLocalPayment(res.payment);
      toast.success(t('payments.method_changed', 'Payment method updated'));
      setChanging(false);
      onChanged?.();
    } catch (e) {
      toast.warn(typeof e === 'string' ? e : t('payments.update_failed', 'Could not update payment'));
    }
  };

  return (
    <div data-testid="completed-payment-card" className="w-full rounded-2xl border border-gray-200 p-4 mb-4 sm:mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Wallet className="w-4 h-4 text-[#4ABDBD]" />
        <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
          {t('payments.payment_information', 'Payment information')}
        </h2>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-500">{t('payments.method', 'Method')}:</span>
          <span data-testid="payment-card-method" className="text-sm font-semibold text-gray-700">{methodLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-500">{t('payments.timing', 'When to pay')}:</span>
          <span className="text-sm font-semibold text-gray-700">{timingLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-500">{t('medical_history.status_label', 'Status')}:</span>
          <span
            data-testid="payment-card-status"
            className={`inline-flex items-center gap-1 text-sm font-semibold ${paid ? 'text-green-600' : 'text-amber-600'}`}
          >
            {paid ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
            {paid ? t('payments.paid', 'Paid') : t('payments.not_paid', 'Not paid')}
          </span>
        </div>
      </div>

      {canEdit && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            data-testid="payment-card-toggle-paid"
            onClick={togglePaid}
            disabled={busy}
            className={`px-4 py-2 rounded-xl text-white text-sm font-semibold shadow-sm transition-all ${
              busy ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md hover:scale-[1.02]'
            } ${paid ? 'bg-gradient-to-r from-amber-500 to-amber-600' : 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A]'}`}
          >
            {paid ? t('payments.mark_not_paid', 'Mark as not paid') : t('payments.mark_paid', 'Mark as paid')}
          </button>

          <button
            type="button"
            data-testid="payment-card-change-method"
            onClick={() => setChanging((v) => !v)}
            disabled={busy}
            className="px-4 py-2 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:border-[#4ABDBD] transition-all flex items-center gap-1.5"
          >
            <Pencil className="w-3.5 h-3.5" />
            {t('payments.change_method', 'Change method')}
          </button>
        </div>
      )}

      {canEdit && changing && (
        <div data-testid="payment-card-method-options" className="mt-3">
          <p className="text-xs font-medium text-gray-500 mb-1.5">
            {t('payments.choose_method', 'Choose a method')}
          </p>
          <div className="flex flex-wrap gap-2">
            {methods.map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => pickMethod(m.key)}
                disabled={busy}
                data-testid={`payment-card-method-${m.key}`}
                className={`text-sm px-3 py-2 rounded-xl border transition ${
                  payment.method === m.key
                    ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8] font-semibold'
                    : 'border-gray-200 text-gray-600 hover:border-[#4ABDBD]'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {timing === 'prepaid' && (
            <p className="text-[11px] text-amber-600 mt-1.5">
              {t('payments.prepaid_no_cash', 'This was an advance (online) appointment — cash is not allowed.')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
